import React, { useState } from "react";
import {
  Box, Card, Grid, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Button, TextField, MenuItem, Dialog, IconButton,
  CircularProgress, Divider, Alert,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { useParams, useNavigate } from "react-router-dom";
import { useGetShopInvoiceQuery, useAddShopInvoicePaymentMutation, useDeleteShopInvoicePaymentMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import dayjs from "dayjs";

const STATUS_CHIP: Record<string, { label: string; bg: string; color: string }> = {
  unpaid:  { label: "Unpaid",  bg: "#ffebee", color: "#c62828" },
  partial: { label: "Partial", bg: "#fff8e1", color: "#f57f17" },
  paid:    { label: "Paid",    bg: "#e8f5e9", color: "#2e7d32" },
};

const Index = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, isLoading, refetch } = useGetShopInvoiceQuery(Number(id));
  const [addPayment] = useAddShopInvoicePaymentMutation();
  const [deletePayment] = useDeleteShopInvoicePaymentMutation();

  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [payDate, setPayDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [payNotes, setPayNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const inv = data?.data;

  const handleAddPayment = async () => {
    if (!inv || !payAmount) return;
    setSaving(true);
    try {
      const res = await addPayment({
        invoiceId: inv.id,
        amount: parseFloat(payAmount),
        payment_method: payMethod,
        payment_date: payDate,
        notes: payNotes || undefined,
      }).unwrap();
      if (res.status || (res as any).success) {
        showMessage("Payment recorded");
        setPayOpen(false); setPayAmount(""); setPayNotes("");
        refetch();
      }
    } catch (e: any) {
      showErrorMessage(e.data?.message || "Failed to add payment");
    } finally { setSaving(false); }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      await deletePayment(paymentId).unwrap();
      showMessage("Payment removed");
      refetch();
    } catch { showErrorMessage("Failed to remove payment"); }
  };

  const handlePrint = () => {
    if (!inv) return;
    const shopName = inv.shop?.customerCompanyName || "Restaurant";
    const contactName = [inv.shop?.customerFirstName, inv.shop?.customerLastName].filter(Boolean).join(" ");
    const addr = [inv.shop?.customerStreetNumber, inv.shop?.customerStreetName].filter(Boolean).join(" ");
    const city = [inv.shop?.customerCity, inv.shop?.customerProvince, inv.shop?.customerPostalCode].filter(Boolean).join(", ");
    const phone = inv.shop?.customerCompanyPhoneNumebr || inv.shop?.customerMobileNum || "";
    const email = inv.shop?.customerCompanyEmail || inv.shop?.customerEmail || "";

    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>${inv.invoice_number}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#2d2d2d;padding:48px 40px;max-width:800px;margin:auto}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;padding-bottom:20px;border-bottom:3px solid #1a237e}
.header h1{font-size:28px;font-weight:800;color:#1a237e}.header .right{text-align:right}
.inv-num{font-size:20px;font-weight:700;color:#1a237e}.inv-date{font-size:13px;color:#666;margin-top:4px}
.inv-status{display:inline-block;margin-top:8px;padding:4px 14px;border-radius:20px;font-size:11px;font-weight:700;text-transform:uppercase;background:#e8f5e9;color:#2e7d32}
.bill{display:flex;justify-content:space-between;margin-bottom:32px}.bill-box h3{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#999;margin-bottom:8px;font-weight:700}
.bill-box p{font-size:13px;line-height:1.7;color:#444}.bill-box .name{font-size:15px;font-weight:700;color:#1a237e}
table{width:100%;border-collapse:collapse;margin-bottom:24px}th{text-align:left;padding:12px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#999;font-weight:700;border-bottom:2px solid #e0e0e0}
td{padding:12px 8px;font-size:13px;border-bottom:1px solid #f0f0f0;color:#444}td.product{font-weight:600;color:#2d2d2d}.right{text-align:right}
.totals{display:flex;justify-content:flex-end;margin-bottom:24px}.totals-box{width:260px}.totals-row{display:flex;justify-content:space-between;padding:6px 0;font-size:13px;color:#666}
.totals-row.grand{border-top:2px solid #1a237e;padding-top:12px;margin-top:4px}.totals-row.grand span{font-size:18px;font-weight:800;color:#1a237e}
.totals-row.paid span{color:#2e7d32;font-weight:600}.totals-row.due span{color:#c62828;font-weight:700}
.payments{margin-bottom:24px}.payments h3{font-size:12px;text-transform:uppercase;letter-spacing:.8px;color:#1a237e;margin-bottom:8px;font-weight:700}
.notes{padding:16px 20px;background:#f8f9ff;border-radius:8px;border-left:4px solid #1a237e;margin-bottom:24px}.notes h4{font-size:11px;text-transform:uppercase;letter-spacing:.8px;color:#1a237e;margin-bottom:6px;font-weight:700}.notes p{font-size:13px;color:#555;line-height:1.6}
.footer{text-align:center;padding-top:24px;border-top:1px solid #e0e0e0}.footer p{font-size:11px;color:#bbb}@media print{body{padding:20px}}</style></head><body>
<div class="header"><div><h1>INVOICE</h1><p style="font-size:12px;color:#888">Order confirmation & delivery summary</p></div><div class="right"><div class="inv-num">${inv.invoice_number}</div><div class="inv-date">${dayjs(inv.invoice_date).format("MMMM D, YYYY")}</div><div class="inv-status">${inv.payment_status}</div></div></div>
<div class="bill"><div class="bill-box"><h3>Bill To</h3><p class="name">${shopName}</p>${contactName?`<p>${contactName}</p>`:""}${addr?`<p>${addr}</p>`:""}${city?`<p>${city}</p>`:""}${phone?`<p>${phone}</p>`:""}${email?`<p>${email}</p>`:""}</div><div class="bill-box" style="text-align:right"><h3>Invoice Details</h3><p>Invoice: ${inv.invoice_number}</p><p>Order: #${inv.shop_order_id}</p><p>Date: ${dayjs(inv.invoice_date).format("MMM D, YYYY")}</p><p>Items: ${inv.items.length}</p></div></div>
<table><thead><tr><th style="width:36px">#</th><th>Product</th><th class="right" style="width:70px">Qty</th><th class="right" style="width:60px">Unit</th><th class="right" style="width:90px">Price</th><th class="right" style="width:100px">Subtotal</th></tr></thead><tbody>
${inv.items.map((it: any,i: number)=>`<tr><td>${i+1}</td><td class="product">${it.name}</td><td class="right">${it.qty}</td><td class="right">${it.unit||"-"}</td><td class="right">$${Number(it.price).toFixed(2)}</td><td class="right">$${Number(it.subtotal).toFixed(2)}</td></tr>`).join("")}
</tbody></table>
<div class="totals"><div class="totals-box"><div class="totals-row"><span>Subtotal</span><span>$${inv.total.toFixed(2)}</span></div><div class="totals-row paid"><span>Amount Paid</span><span>$${inv.amount_paid.toFixed(2)}</span></div><div class="totals-row due"><span>Balance Due</span><span>$${inv.balance_due.toFixed(2)}</span></div><div class="totals-row grand"><span>Total</span><span>$${inv.total.toFixed(2)}</span></div></div></div>
${inv.payments&&inv.payments.length>0?`<div class="payments"><h3>Payment History</h3><table><thead><tr><th>Date</th><th>Method</th><th class="right">Amount</th><th>Notes</th></tr></thead><tbody>${inv.payments.map((p:any)=>`<tr><td>${dayjs(p.payment_date).format("MMM D, YYYY")}</td><td>${p.payment_method}</td><td class="right">$${Number(p.amount).toFixed(2)}</td><td>${p.notes||"-"}</td></tr>`).join("")}</tbody></table></div>`:""}
${inv.notes?`<div class="notes"><h4>Notes</h4><p>${inv.notes}</p></div>`:""}
<div class="footer"><p>Thank you for your business</p></div>
<script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  if (isLoading || !inv) return <Box display="flex" justifyContent="center" alignItems="center" height="30vh"><CircularProgress /></Box>;

  const s = STATUS_CHIP[inv.payment_status] || STATUS_CHIP.unpaid;

  return (
    <Box sx={{ mt: 1 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/shop-invoice/list")}
        sx={{ mb: 2, textTransform: "none", color: "#666" }}>
        Back to Invoices
      </Button>

      <Grid container spacing={2}>
        {/* Left: Invoice details */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
            {/* Header */}
            <Box sx={{ bgcolor: "#1a237e", px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box>
                <Typography fontWeight={800} fontSize={20} color="#fff">{inv.invoice_number}</Typography>
                <Typography fontSize={12} color="rgba(255,255,255,0.6)">
                  Order #{inv.shop_order_id} &middot; {dayjs(inv.invoice_date).format("MMMM D, YYYY")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Chip label={s.label} size="small" sx={{ fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />
                <Button variant="outlined" size="small" startIcon={<PrintIcon sx={{ fontSize: 14 }} />} onClick={handlePrint}
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 600, textTransform: "none", borderRadius: 1.5 }}>
                  Print
                </Button>
              </Box>
            </Box>

            {/* Restaurant info */}
            <Box sx={{ px: 3, py: 2, bgcolor: "#f8f9ff", borderBottom: "1px solid #e0e0e0" }}>
              <Typography fontSize={11} color="text.disabled" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>Restaurant</Typography>
              <Typography fontSize={15} fontWeight={700} color="#1a237e">{(inv.shop as any)?.customerCompanyName || `Shop #${inv.shop_id}`}</Typography>
              <Typography fontSize={12} color="text.secondary">
                {[(inv.shop as any)?.customerMobileNum, (inv.shop as any)?.customerCompanyEmail || (inv.shop as any)?.customerEmail].filter(Boolean).join(" | ")}
              </Typography>
            </Box>

            {/* Items */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#fafafa" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#888", width: 36 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#888" }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#888", textAlign: "right", width: 70 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#888", textAlign: "right", width: 60 }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#888", textAlign: "right", width: 90 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#888", textAlign: "right", width: 100 }}>Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inv.items.map((it: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: 12, color: "#999" }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{it.name}</TableCell>
                      <TableCell sx={{ fontSize: 13, textAlign: "right" }}>{it.qty}</TableCell>
                      <TableCell sx={{ fontSize: 12, textAlign: "right", color: "#888" }}>{it.unit || "-"}</TableCell>
                      <TableCell sx={{ fontSize: 13, textAlign: "right" }}>${Number(it.price).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>${Number(it.subtotal).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 3, py: 2 }}>
              <Box sx={{ width: 240 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography fontSize={13} color="text.secondary">Subtotal</Typography>
                  <Typography fontSize={13}>${inv.total.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography fontSize={13} color="#2e7d32" fontWeight={600}>Paid</Typography>
                  <Typography fontSize={13} color="#2e7d32" fontWeight={600}>${inv.amount_paid.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                  <Typography fontSize={15} fontWeight={700} color={inv.balance_due > 0 ? "#c62828" : "#2e7d32"}>Balance Due</Typography>
                  <Typography fontSize={15} fontWeight={800} color={inv.balance_due > 0 ? "#c62828" : "#2e7d32"}>${inv.balance_due.toFixed(2)}</Typography>
                </Box>
              </Box>
            </Box>

            {inv.notes && (
              <Box sx={{ mx: 3, mb: 2, p: 2, bgcolor: "#f8f9ff", borderRadius: 2, borderLeft: "4px solid #1a237e" }}>
                <Typography fontSize={11} color="#1a237e" fontWeight={700} textTransform="uppercase" letterSpacing={0.5} mb={0.5}>Notes</Typography>
                <Typography fontSize={13} color="text.secondary">{inv.notes}</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Right: Payments */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, p: 2.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography fontWeight={700} fontSize={15}>Payments</Typography>
              {inv.balance_due > 0 && (
                <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => { setPayAmount(String(inv.balance_due.toFixed(2))); setPayOpen(true); }}
                  sx={{ fontSize: 11, fontWeight: 600, textTransform: "none", borderRadius: 1.5, bgcolor: "#1a237e" }}>
                  Add Payment
                </Button>
              )}
            </Box>

            {inv.payment_status === "paid" && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>Fully paid</Alert>
            )}

            {(!inv.payments || inv.payments.length === 0) ? (
              <Typography fontSize={13} color="text.disabled" textAlign="center" py={3}>No payments recorded</Typography>
            ) : (
              inv.payments.map((p: any) => (
                <Box key={p.id} sx={{ display: "flex", alignItems: "center", py: 1.25, borderBottom: "1px solid #f0f0f0" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontSize={13} fontWeight={600}>${Number(p.amount).toFixed(2)}</Typography>
                    <Typography fontSize={11} color="text.secondary">
                      {dayjs(p.payment_date).format("MMM D, YYYY")} &middot; {p.payment_method}
                    </Typography>
                    {p.notes && <Typography fontSize={11} color="text.disabled" fontStyle="italic">{p.notes}</Typography>}
                  </Box>
                  <IconButton size="small" onClick={() => handleDeletePayment(p.id)} sx={{ color: "#ccc", "&:hover": { color: "#d32f2f" } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography fontSize={13} fontWeight={600}>Total Paid</Typography>
              <Typography fontSize={13} fontWeight={700} color="#2e7d32">${inv.amount_paid.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
              <Typography fontSize={13} fontWeight={600}>Remaining</Typography>
              <Typography fontSize={13} fontWeight={700} color={inv.balance_due > 0 ? "#c62828" : "#2e7d32"}>${inv.balance_due.toFixed(2)}</Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Add Payment Dialog */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} fontSize={16} mb={2}>Record Payment</Typography>
          <TextField size="small" fullWidth label="Amount" type="number" value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)} sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: "#999" }}>$</Typography> }}
          />
          <TextField size="small" fullWidth select label="Payment Method" value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)} sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
            <MenuItem value="cheque">Cheque</MenuItem>
            <MenuItem value="e_transfer">E-Transfer</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField size="small" fullWidth label="Payment Date" type="date" value={payDate}
            onChange={(e) => setPayDate(e.target.value)} sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField size="small" fullWidth label="Notes (optional)" value={payNotes}
            onChange={(e) => setPayNotes(e.target.value)} sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => setPayOpen(false)} sx={{ borderRadius: 2, textTransform: "none", borderColor: "#ccc", color: "#666" }}>Cancel</Button>
            <Button variant="contained" disabled={saving || !payAmount} onClick={handleAddPayment}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, bgcolor: "#1a237e" }}>
              {saving ? "Saving..." : "Record Payment"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Index;
